import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../api';
import { useOptionalResponseArtifactEvents } from '../../../providers/ResponseArtifactEventProvider';
import {
  HotelArtifactBookingState,
  HotelBookingGuestDetails,
  isSettledBookingState,
  readHotelBookingState,
} from './hotelArtifact';

interface ArtifactSnapshot {
  hotelBooking: HotelArtifactBookingState | null;
  hotelGuest: HotelBookingGuestDetails | null;
}

interface ArtifactFetchResponse {
  success: boolean;
  artifact?: { userResponse?: unknown };
  hotelGuest?: HotelBookingGuestDetails;
}

const POLL_MS = 2500;
/** While parked on the saved-card payment (ops act within hours, not seconds). */
const PARKED_POLL_MS = 15 * 1000;
const POLL_CEILING_MS = 5 * 60 * 1000;

/**
 * Reads the hotel artifact from the endpoint that fits where we are: the
 * public share endpoint on `/artifact/:token`, the authenticated one in-app.
 */
export function useHotelArtifactFetcher(
  artifactId: string,
  shareToken?: string,
): () => Promise<ArtifactSnapshot> {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  return useCallback(async () => {
    const response = shareToken
      ? await api.get<ArtifactFetchResponse>(
          `/public/artifacts/${encodeURIComponent(shareToken)}`,
        )
      : await api.get<ArtifactFetchResponse>(
          `/response-artifacts/artifact/${encodeURIComponent(artifactId)}`,
          isAuthenticated
            ? {
                headers: {
                  Authorization: `Bearer ${await getAccessTokenSilently()}`,
                },
              }
            : undefined,
        );
    const data = response.data;
    return {
      hotelBooking: readHotelBookingState(data.artifact?.userResponse),
      hotelGuest: data.hotelGuest ?? null,
    };
  }, [artifactId, shareToken, getAccessTokenSilently, isAuthenticated]);
}

/**
 * Live `userResponse.hotelBooking` for an artifact while a Book press is in
 * flight: Pusher `RESPONSE_ARTIFACT_UPDATED` when a channel is available
 * (in-app), polling otherwise (public share page) and as a safety net.
 *
 * `attempt` identifies the press being watched (null = nothing in flight);
 * a new value starts clean, and the settled result of the last attempt is
 * kept afterwards so the view can show it until the host hands down a
 * refreshed artifact.
 */
export function useHotelBookingState(
  artifactId: string,
  attempt: number | null,
  fetchSnapshot: () => Promise<ArtifactSnapshot>,
): HotelArtifactBookingState | null {
  const [state, setState] = useState<HotelArtifactBookingState | null>(null);
  const events = useOptionalResponseArtifactEvents();
  const settledRef = useRef(false);
  const parkedRef = useRef(false);

  useEffect(() => {
    if (attempt == null) return;
    settledRef.current = false;
    parkedRef.current = false;
    setState(null);
  }, [attempt]);

  const apply = useCallback((next: HotelArtifactBookingState | null) => {
    if (!next || settledRef.current) return;
    if (isSettledBookingState(next)) settledRef.current = true;
    parkedRef.current = next.state === 'awaiting_payment';
    setState(next);
  }, []);

  useEffect(() => {
    if (attempt == null || !events) return;
    const callbackId = `hotel-booking-${artifactId}`;
    events.registerUpdatedCallback(
      callbackId,
      (data: { artifactId?: string; userResponse?: unknown }) => {
        if (data?.artifactId !== artifactId) return;
        apply(readHotelBookingState(data.userResponse));
      },
    );
    return () => events.unregisterUpdatedCallback(callbackId);
  }, [attempt, events, artifactId, apply]);

  useEffect(() => {
    if (attempt == null) return;
    let cancelled = false;
    let startedAt = Date.now();
    let wasParked = false;
    const tick = async () => {
      if (cancelled || settledRef.current) return;
      if (parkedRef.current && !wasParked) {
        // Parking is a fresh wait, not the tail of the Book press.
        wasParked = true;
        startedAt = Date.now();
      }
      if (Date.now() - startedAt > POLL_CEILING_MS) return;
      try {
        const snapshot = await fetchSnapshot();
        if (!cancelled) apply(snapshot.hotelBooking);
      } catch {
        // transient; the next tick retries
      }
      if (!cancelled && !settledRef.current) {
        timer = window.setTimeout(
          tick,
          parkedRef.current ? PARKED_POLL_MS : POLL_MS,
        );
      }
    };
    let timer = window.setTimeout(tick, POLL_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [attempt, fetchSnapshot, apply]);

  return state;
}
